/**
 * RoleProfileEditModal — Principal-tier edit form for one role profile.
 *
 * Deliberately a plain field-by-field form, not a rich structured editor — the content itself
 * is a first-draft reconstruction (see the seed fixture's review notes in docs/ROLE_PROFILES.md)
 * that Matt needs to correct against his own memory of the original docs, so what matters is that
 * every field is editable, not that the editing experience is polished. Section arrays
 * (responsibilities, decision_authority, operational_rhythm, success_metrics) use small
 * add/remove row editors; qualifications/boundaries/callouts are one-item-per-line textareas.
 */

import { useState, useEffect } from 'react'
import { updateRoleProfile, setRoleHolders } from '../services/roleProfiles.js'
import { getTeamMembers } from '../services/members.js'

export default function RoleProfileEditModal({ role, allRoles, onClose, onSaved }) {
  const [roleTitle, setRoleTitle] = useState(role.role_title || '')
  // Who holds the role: real people, picked from the team (database/278, Matt 2026-09-28), never typed.
  const [holderIds, setHolderIds] = useState(() => new Set((role.holders || []).map(h => h.memberId)))
  const [team, setTeam] = useState(null)
  useEffect(() => { getTeamMembers().then(setTeam).catch(() => setTeam([])) }, [])
  const [status, setStatus] = useState(role.status || 'active')
  const [reportsTo, setReportsTo] = useState(role.reports_to || '')
  // Dotted lines (database/320): roles this one also takes direction from, besides the one it reports to.
  const [directionFrom, setDirectionFrom] = useState(() => new Set(role.direction_from || []))
  const [purposeStatement, setPurposeStatement] = useState(role.purpose_statement || '')
  const [responsibilities, setResponsibilities] = useState(role.responsibilities || [])
  const [decisionAuthority, setDecisionAuthority] = useState(role.decision_authority || [])
  const [operationalRhythm, setOperationalRhythm] = useState(role.operational_rhythm || [])
  const [successMetrics, setSuccessMetrics] = useState(role.success_metrics || [])
  const [qualifications, setQualifications] = useState((role.qualifications || []).join('\n'))
  const [boundaries, setBoundaries] = useState((role.boundaries || []).join('\n'))
  const [callouts, setCallouts] = useState((role.callouts || []).join('\n'))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const otherRoles = (allRoles || []).filter(r => r.role_key !== role.role_key)

  function linesOf(text) { return text.split('\n').map(s => s.trim()).filter(Boolean) }

  async function save() {
    setSaving(true); setError('')
    try {
      const updated = await updateRoleProfile(role.role_key, {
        role_title: roleTitle.trim() || role.role_title,
        status,
        reports_to: reportsTo || null,
        direction_from: [...directionFrom].filter(k => k !== reportsTo),
        purpose_statement: purposeStatement.trim() || null,
        responsibilities: responsibilities.length ? responsibilities : null,
        decision_authority: decisionAuthority.length ? decisionAuthority : null,
        operational_rhythm: operationalRhythm.length ? operationalRhythm : null,
        success_metrics: successMetrics.length ? successMetrics : null,
        qualifications: linesOf(qualifications).length ? linesOf(qualifications) : null,
        boundaries: linesOf(boundaries).length ? linesOf(boundaries) : null,
        callouts: linesOf(callouts).length ? linesOf(callouts) : null,
      })
      const byId = Object.fromEntries((team || []).map(m => [m.id, m.name]))
      for (const h of role.holders || []) if (!byId[h.memberId]) byId[h.memberId] = h.name
      const holders = [...holderIds].map(id => ({ memberId: id, name: byId[id] })).filter(h => h.name)
      const withHolders = team === null ? updated : await setRoleHolders(role.role_key, holders)
      onSaved({ ...updated, ...withHolders })
    } catch (e) {
      setError(e.message || 'Save failed.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={M.overlay} onClick={onClose}>
      <div style={M.card} onClick={e => e.stopPropagation()}>
        <div style={M.head}>
          <span style={M.title}>Edit {role.role_title} Profile</span>
          <button style={M.x} onClick={onClose}>&times;</button>
        </div>
        <div style={M.body}>
          {error && <div style={M.notice}>{error}</div>}

          <div style={M.row}>
            <Field label="Role title" style={{ flex: 1 }}>
              <input style={M.input} value={roleTitle} onChange={e => setRoleTitle(e.target.value)} />
            </Field>
            <Field label="Held by" style={{ flex: 1 }}>
              {team === null ? <div style={{ fontSize: 12.5, color: 'var(--aj-text-muted)' }}>Loading the team…</div> : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', maxHeight: 132, overflowY: 'auto', padding: '4px 0' }}>
                  {team.map(m => (
                    <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--aj-text)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={holderIds.has(m.id)}
                        onChange={e => setHolderIds(prev => { const n = new Set(prev); if (e.target.checked) n.add(m.id); else n.delete(m.id); return n })} />
                      {m.name}
                    </label>
                  ))}
                </div>
              )}
            </Field>
          </div>

          <div style={M.row}>
            <Field label="Status" style={{ flex: 1 }}>
              <select style={M.input} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
            <Field label="Reports to" style={{ flex: 1 }}>
              <select style={M.input} value={reportsTo} onChange={e => setReportsTo(e.target.value)}>
                <option value="">None (top of org chart)</option>
                {otherRoles.map(r => <option key={r.role_key} value={r.role_key}>{r.role_title}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Also takes direction from (a dotted line on the chart)">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', padding: '4px 0' }}>
              {otherRoles.filter(r => r.status !== 'archived' && r.role_key !== reportsTo).map(r => (
                <label key={r.role_key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--aj-text)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={directionFrom.has(r.role_key)}
                    onChange={e => setDirectionFrom(prev => { const n = new Set(prev); if (e.target.checked) n.add(r.role_key); else n.delete(r.role_key); return n })} />
                  {r.role_title}
                </label>
              ))}
            </div>
          </Field>

          <Field label="Purpose statement">
            <textarea style={{ ...M.input, minHeight: 80 }} value={purposeStatement} onChange={e => setPurposeStatement(e.target.value)} placeholder="Role-specific framing — not a firm mission statement." />
          </Field>

          <DomainsField label="Responsibilities" value={responsibilities} onChange={setResponsibilities} />
          <RowsField label="Decision Authority" value={decisionAuthority} onChange={setDecisionAuthority}
            fields={[{ key: 'role_decides', label: 'Role decides' }, { key: 'involves_president', label: 'Escalates to' }]} />
          <RowsField label="Operational Rhythm" value={operationalRhythm} onChange={setOperationalRhythm}
            fields={[{ key: 'cadence', label: 'Cadence' }, { key: 'purpose', label: 'Purpose' }]} />
          <RowsField label="Success Metrics" value={successMetrics} onChange={setSuccessMetrics}
            fields={[{ key: 'domain', label: 'Domain' }, { key: 'fully_executing', label: 'Fully executing' }, { key: 'kpi', label: 'KPI' }]} />

          <Field label="Qualifications (one per line)">
            <textarea style={{ ...M.input, minHeight: 80 }} value={qualifications} onChange={e => setQualifications(e.target.value)} />
          </Field>
          <Field label="Boundaries — what this role does NOT own (one per line)">
            <textarea style={{ ...M.input, minHeight: 60 }} value={boundaries} onChange={e => setBoundaries(e.target.value)} />
          </Field>
          <Field label="Callouts — pull quotes (one per line)">
            <textarea style={{ ...M.input, minHeight: 60 }} value={callouts} onChange={e => setCallouts(e.target.value)} />
          </Field>
        </div>
        <div style={M.foot}>
          <button style={M.ghost} onClick={onClose}>Cancel</button>
          <button style={M.primary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children, style }) {
  return (
    <div style={{ ...M.field, ...style }}>
      <div style={M.fieldLabel}>{label}</div>
      {children}
    </div>
  )
}

/** responsibilities: [{ domain, items: string[] }] */
function DomainsField({ label, value, onChange }) {
  function setDomain(i, domain) { onChange(value.map((g, j) => j === i ? { ...g, domain } : g)) }
  function setItems(i, text) { onChange(value.map((g, j) => j === i ? { ...g, items: text.split('\n') } : g)) }
  function remove(i) { onChange(value.filter((_, j) => j !== i)) }
  function add() { onChange([...value, { domain: '', items: [] }]) }

  return (
    <Field label={label}>
      {value.map((g, i) => (
        <div key={i} style={M.subRow}>
          <input style={{ ...M.input, marginBottom: 6 }} value={g.domain || ''} onChange={e => setDomain(i, e.target.value)} placeholder="Domain name" />
          <textarea style={{ ...M.input, minHeight: 60 }} value={(g.items || []).join('\n')} onChange={e => setItems(i, e.target.value)} placeholder="One responsibility per line" />
          <button style={M.removeBtn} onClick={() => remove(i)}>Remove domain</button>
        </div>
      ))}
      <button style={M.addBtn} onClick={add}>+ Add domain</button>
    </Field>
  )
}

/** [{ [field.key]: string }] — generic N-column row editor. */
function RowsField({ label, value, onChange, fields }) {
  function setField(i, key, v) { onChange(value.map((row, j) => j === i ? { ...row, [key]: v } : row)) }
  function remove(i) { onChange(value.filter((_, j) => j !== i)) }
  function add() { onChange([...value, Object.fromEntries(fields.map(f => [f.key, '']))]) }

  return (
    <Field label={label}>
      {value.map((row, i) => (
        <div key={i} style={M.subRow}>
          <div style={M.row}>
            {fields.map(f => (
              <input key={f.key} style={{ ...M.input, flex: 1 }} value={row[f.key] || ''} onChange={e => setField(i, f.key, e.target.value)} placeholder={f.label} />
            ))}
          </div>
          <button style={M.removeBtn} onClick={() => remove(i)}>Remove row</button>
        </div>
      ))}
      <button style={M.addBtn} onClick={add}>+ Add row</button>
    </Field>
  )
}

const M = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 700, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', overflowY: 'auto' },
  card: { width: '100%', maxWidth: 640, display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.3)', maxHeight: '90vh' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--aj-border-mid)', flexShrink: 0 },
  title: { fontFamily: 'Montserrat,sans-serif', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13, fontWeight: 700, color: 'var(--aj-text)' },
  x: { background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', cursor: 'pointer' },
  body: { padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '0.5px solid var(--aj-border-mid)', flexShrink: 0 },
  field: { display: 'flex', flexDirection: 'column' },
  fieldLabel: { fontFamily: '"DM Mono",monospace', fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 4 },
  input: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)' },
  row: { display: 'flex', gap: 10 },
  subRow: { border: '0.5px solid var(--aj-border)', borderRadius: 6, padding: 10, marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 6 },
  primary: { background: '#07272D', border: 'none', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: '#fff' },
  ghost: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' },
  addBtn: { alignSelf: 'flex-start', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)' },
  removeBtn: { alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: '#c0392b' },
  notice: { background: 'rgba(184,134,11,0.1)', border: '0.5px solid rgba(184,134,11,0.3)', borderRadius: 6, padding: '8px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#8a6508' },
}
